/**
 * useShooglingBatches Hook
 *
 * Fetches production batches that have been inoculated but not yet shoogled (mixed).
 * Used by the Shoogling operational dashboard.
 *
 * "Shoogling" is the process of mixing/tumbling the spawn with the substrate
 * in the bags before placing them in incubation.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call production-operations Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callProductionOperations(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('production-operations', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch production batches in INOCULATED status (need shoogling)
 * @returns {Object} { batches, loading, error, refetch }
 */
export function useShooglingBatches() {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('shoogling_list')
      setBatches(response.data || [])
    } catch (err) {
      console.error('[useShooglingBatches] Error:', err)
      setError(err.message)
      setBatches([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    batches,
    loading,
    error,
    refetch: fetchBatches
  }
}

/**
 * Hook for shoogling mutations
 * @returns {Object} { markAsShoogled, loading, error }
 */
export function useShooglingMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Mark a production batch as shoogled (INOCULATED -> SHOOGLED)
   * @param {string} productionBatchId - Production batch UUID
   * @returns {Promise<Object>} Updated batch
   */
  const markAsShoogled = useCallback(async (productionBatchId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('mark_shoogled', {
        production_batch_id: productionBatchId
      })
      return response.data
    } catch (err) {
      console.error('[useShooglingMutations.markAsShoogled] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const undoShoogled = useCallback(async (productionBatchId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('undo_shoogled', {
        production_batch_id: productionBatchId
      })
      return response.data
    } catch (err) {
      console.error('[useShooglingMutations.undoShoogled] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    markAsShoogled,
    undoShoogled,
    loading,
    error
  }
}

export default useShooglingBatches
